import { FilterControls } from '@/dashboards/SpendSummary/components/ActionBar/FilterControls';
import { FilterNode } from '@/dashboards/SpendSummary/SpendSummary';
import { SpendSummarySearch } from '@/dashboards/SpendSummary';
import {
    useSpendSummaryContext,
    useSpendSummaryUrl,
} from '@/dashboards/SpendSummary/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Drawer, useMatchMedia } from '@analytical-alley/ui';
import moment from 'moment/moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HiAdjustments } from 'react-icons/hi';

const STORAGE_KEY = 'spendSummarySearch';

export const ActionBar = () => {
    const { isLg } = useMatchMedia(['lg']);
    const { search, setSearch } = useSpendSummaryUrl();

    const { startDate, endDate } = useDashboardGlobalContext();

    const { spendSummaryData, setSelectedNodes } = useSpendSummaryContext();

    const storedSearchString = window.sessionStorage.getItem(STORAGE_KEY);
    const storedSearch: SpendSummarySearch = useMemo(
        () => (storedSearchString ? JSON.parse(storedSearchString) : {}),
        [storedSearchString],
    );

    const onSetSearch = (newSearch: Partial<SpendSummarySearch>) => {
        setSearch(newSearch);
        window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                ...storedSearch,
                ...newSearch,
            }),
        );
    };

    const onSetSelectedNodes = (nodes: FilterNode[]) => {
        setSelectedNodes(nodes);
    };

    const nodes = useMemo(() => {
        return search.modelBreakdown === 'category'
            ? spendSummaryData.treeNodes.map((node) => ({
                  ...node,
                  children: [],
              }))
            : spendSummaryData.treeNodes;
    }, [spendSummaryData.treeNodes, search.modelBreakdown]);

    const minDate = moment.utc(startDate, 'DD-MM-YYYY').toDate();
    const maxDate = moment.utc(endDate, 'DD-MM-YYYY').toDate();

    const canReset = useMemo(
        () =>
            (storedSearch.startDate && storedSearch.startDate !== startDate) ||
            (storedSearch.endDate && storedSearch.endDate !== endDate),
        [endDate, startDate, storedSearch.endDate, storedSearch.startDate],
    );

    useEffect(() => {
        if (search.startDate === startDate && storedSearch.startDate) {
            setSearch({
                startDate: storedSearch.startDate,
            });
        }

        if (search.endDate === endDate && storedSearch.endDate) {
            setSearch({
                endDate: storedSearch.endDate,
            });
        }
    }, [search, setSearch, startDate, endDate, storedSearch]);

    const onResetClick = useCallback(() => {
        window.sessionStorage.removeItem(STORAGE_KEY);
        setSearch({
            startDate: undefined,
            endDate: undefined,
        });
    }, [setSearch]);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const controlProps = {
        search,
        nodes,
        minDate,
        maxDate,
        canReset: !!canReset,
        onSetSearch,
        onSetSelectedNodes,
        onResetClick,
    };

    if (!isLg) {
        return (
            <>
                <button
                    className="glass-tile self-center p-2 rounded-md shadow-lg hover:bg-white/30"
                    onClick={() => setIsDrawerOpen(true)}
                    aria-label="Open filters"
                >
                    <HiAdjustments className="w-5 h-5" />
                </button>

                <Drawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    position="top"
                >
                    <Drawer.Header title="Filters" />
                    <Drawer.Items>
                        <FilterControls {...controlProps} vertical />
                    </Drawer.Items>
                </Drawer>
            </>
        );
    }

    return <FilterControls {...controlProps} />;
};
